'use client';

import React, { useEffect, useState } from 'react';
import { useFooter } from '@/contexts/FooterContext';
import { SectionTitle } from '@/components/ui/insights/section-title';
import { AnimatedSection } from '@/components/ui/animated-section';
import {
  CollectorStats,
  TierDistribution,
  BadgeDistribution,
  LeaderboardSidebar,
} from '@/components/ui/leaderboard';
import { motion } from 'framer-motion';
import { TwoColumnLayout } from '@/components/ui/layout/two-column-layout';
import { OverlayText } from '@/components/ui/common/overlay-text';
import { Collectors } from '@/components/ui/leaderboard/collectors';
import { CollectorsAll } from '@/components/ui/leaderboard/collectors-all';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

export default function LeaderboardPage({ alldata, collectorData }) {

  const { setShowFooter } = useFooter();
  useEffect(() => {
    // Footer'ı gizle
    setShowFooter(false);

    // Sayfa değiştiğinde footer'ı tekrar göster
    return () => setShowFooter(true);
  }, [setShowFooter]);

  const [selectedTier, setSelectedTier] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="w-full h-full py-2.5 px-[15px] font-mono text-[10px]">
      <TwoColumnLayout
        mainContent={
          <div className="pr-0 md:pr-0 flex-1">
            {/* Header section */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <OverlayText className="w-[50%]" text={'Collectors Leaderboard'} />
            </motion.div>

            {/* Stats Cards */}
            <AnimatedSection className="mb-8" delay={0.1}>
              <SectionTitle className="mb-4">[ HIGHLIGHTS ]</SectionTitle>
              <CollectorStats alldata={alldata} />
            </AnimatedSection>

            {/* Tier and Badge Distribution */}
            <AnimatedSection className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" delay={0.2}>
              <TierDistribution alldata={alldata} />
              <BadgeDistribution alldata={alldata} />
            </AnimatedSection>

            {/* Most Active Collectors */}
            <AnimatedSection className="mb-8" delay={0.4}>
              <div className="mb-6">
                <SectionTitle>[ MOST ACTIVE COLLECTORS ]</SectionTitle>
              </div>
              <Collectors
                collectors={collectorData.mostActiveCollectors}
                tiers={collectorData?.tiersData}
                showRank={false}
                showDetails={false}
                removeBorders={true}
                hideIds={true}
                expandable={false}
              />
            </AnimatedSection>

            {/* All Collectors */}
            <AnimatedSection className="mb-8" delay={0.5}>
              <div className="flex items-center justify-between mb-6">
                <SectionTitle>[ ALL COLLECTORS ]</SectionTitle>
                <div className="flex items-center space-x-3">
                  {/* Filter Dropdown */}
                  <div className="relative">
                    <select
                      id="tier-filter"
                      className="appearance-none text-[10px] bg-zinc-100 border border-zinc-200 rounded-md py-1 pl-2 pr-8 focus:outline-hidden focus:ring-0 focus:border-zinc-800"
                      value={selectedTier}
                      onChange={e => {
                        setSelectedTier(e.target.value);
                      }}
                    >
                      <option value="All">All Tiers</option>
                      {collectorData?.tiersData.map(tier => (
                        <option key={tier.id} value={tier.id}>
                          {tier.title}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-zinc-500"
                      >
                        <path d="M8 17l4 4 4-4" />
                        <path d="M8 10h8" />
                        <path d="M8 3h8" />
                      </svg>
                    </div>
                  </div>

                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      id="collector-search"
                      placeholder="Search collectors..."
                      value={searchQuery}
                      className="text-[10px] bg-zinc-100 border border-zinc-200 rounded-md py-1 pl-7 pr-2 w-40 focus:outline-hidden focus:ring-0 focus:border-zinc-800"
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                    <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-zinc-500"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <QueryClientProvider client={queryClient}>
                <CollectorsAll
                  searchQuery={searchQuery}
                  selectedTier={selectedTier}
                />
              </QueryClientProvider>
            </AnimatedSection>
          </div>
        }
        sidebar={<LeaderboardSidebar collectorData={collectorData} />}
      />
    </div>
  );
}
